import { cookiePro as cookieProService } from '@domgen/shared/services/cookiePro';
import { applyGoogleAnalyticsConsentMode } from '@domgen/shared/services/ga/ga';
import { loadExternalScript } from '@domgen/shared/utils/loadExternalScript';
import { log } from '@domgen/shared/utils/log';
import { maintenanceWindowActive } from '@domgen/shared/utils/maintenanceWindowActive/maintenanceWindowActive';
import { PostHogProvider } from '@posthog/react';
import posthog from 'posthog-js';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider } from 'react-router-dom';
import { AppContextProvider } from './context/AppContext';
import { OTELProvider } from './context/otel/provider';
import { StoreEvent } from './enums/storeEvent';
import { RecaptchaError } from './errors/recaptcha';
import { CreateAppConfiguration } from './models/createApp';
import { resolveTenantFeatures } from './models/tenantFeatures';
import { buildRouter } from './routes/routes';
import { initPostHog } from './services/posthog/init';
import { loadRecaptchaScript } from './services/recaptcha';
import { store } from './store/store';
import { initI18Next } from './translations/i18next';
import { getSystemCode } from './utils/getSystemCode';
import { enhanceSpan } from './utils/otel/enhanceTrace';

/**
 * @description
 * The `createApp` function is responsible for initializing and rendering the React application. It performs several key tasks:
 * 1. It logs the app version and initialization details for debugging purposes.
 * 2. It checks for the presence of a root container in the HTML where the app will be rendered, and logs an error if it's missing.
 * 3. It extracts URL parameters to determine the source for analytics tracking.
 * 4. It loads critical third-party services (like reCAPTCHA and PostHog) before rendering the app, ensuring that if any of these services fail to load, it logs the error and prevents the app from rendering to aa broken user experience.
 * 5. If all critical services load successfully, it initializes the app's state management and renders the React application within the OTELProvider for observability and PostHogProvider for analytics.
 * 6. After rendering the app, it starts CookiePro and checkout loading without waiting; Adobe Tags waits for CookiePro preferences when configured.
 *
 * @param {CreateAppConfiguration} config - The configuration object for creating the app, including client logo, environment, brand code, version, reCAPTCHA site key, routes, and other service configurations.
 * @returns {Promise<void>} A promise that resolves when the app has been initialized and rendered successfully, or rejects if there was an error during initialization.
 * @throws {RecaptchaError} Throws a RecaptchaError if the reCAPTCHA script fails to load, which is critical for the app's functionality.
 * @throws {Error} Throws a generic error if there is an unexpected issue during app initialization.
 */
export const createApp = async (config: CreateAppConfiguration) => {
  const {
    clientLogo,
    dGLogo,
    version = 'unknown', // Default to 'unknown' if version is not provided
    recaptchaSiteKey,
    routes,
    environment,
    brandCode,
    serviceName,
    otel,
    cookiePro,
    adobeTags,
    apiBaseUrl,
    posthogConfig,
    isMaintenanceModeEnabled = false,
    checkoutCDN,
    appPath,
    translation,
    tenantFeatures,
  } = config;
  const resolvedTenantFeatures = resolveTenantFeatures(tenantFeatures);

  log.info({ message: `Running version: ${version ?? 'unknown'}` });
  log.debug({ message: 'Starting app initialization', data: { ...config } });

  const container = document.getElementById('root');
  // We should always have a root container in our HTML,
  // but we want to handle the case where it's missing gracefully by showing an error message instead of crashing the app.
  if (!container) {
    // May want to consider a more robust logging solution here, or at least ensuring this gets reported to our monitoring tools,
    // since if the root container is missing it likely indicates a problem with our HTML template which could affect all users of the app.
    // throw new Error('Root container not found');
    log.error({ message: 'Root container not found' });
    return;
  }

  const params = new URLSearchParams(window.location.search);
  const mn = params.get('mn') ?? undefined;
  const sn = params.get('sn') ?? undefined;
  const rs = params.get('rs') ?? undefined;
  const urlParams = mn || sn || rs ? { mn, sn, rs } : undefined;
  const source = getSystemCode(urlParams?.rs);

  log.debug({ message: 'URL Params', data: urlParams }); // Log URL params for debugging
  log.debug({ message: 'Determined source for analytics', data: { source } });

  if (cookiePro?.brandScriptId && cookiePro.src) {
    log.info({ message: 'Initializing CookiePro' });
    cookieProService.configure({
      brandScriptId: cookiePro.brandScriptId,
      src: cookiePro.src,
      scriptId: 'cookiePro',
    });
    let adobeTagsStarted = false;
    cookieProService.subscribe((preferences) => {
      applyGoogleAnalyticsConsentMode(preferences);
      if (
        adobeTagsStarted ||
        !adobeTags?.enabled ||
        !adobeTags.scriptUrl ||
        preferences.status !== 'ready' ||
        !preferences.performance ||
        !preferences.marketing
      ) {
        return;
      }
      adobeTagsStarted = true;
      log.info({ message: 'Loading Adobe Tags script' });
      loadExternalScript({ id: 'adobeTagManager', src: adobeTags.scriptUrl }).catch(
        (error: unknown) => {
          log.error({ message: 'Failed to load Adobe Tags script', data: error });
        },
      );
    });
    cookieProService.initialise().catch((error: unknown) => {
      log.error({ message: 'Failed to initialize CookiePro', data: error });
    });
  } else {
    applyGoogleAnalyticsConsentMode();
    if (adobeTags?.enabled && adobeTags.scriptUrl) {
      log.info({ message: 'Loading Adobe Tags script' });
      loadExternalScript({ id: 'adobeTagManager', src: adobeTags.scriptUrl }).catch(
        (error: unknown) => {
          log.error({ message: 'Failed to load Adobe Tags script', data: error });
        },
      );
    }
  }
  if (checkoutCDN) {
    log.info({ message: 'Loading Checkout SDK script' });
    loadExternalScript({ id: 'checkoutSDK', src: checkoutCDN }).catch((error: unknown) => {
      log.error({ message: 'Failed to load Checkout SDK script', data: error });
    });
  }

  Promise.all([
    // Recaptcha is required for the app to function properly, so we want to load it before rendering the app.
    // If it fails to load, we should show an error message.
    loadRecaptchaScript(recaptchaSiteKey),
    initPostHog({
      brand: brandCode,
      source,
      host: posthogConfig.host,
      key: posthogConfig.key,
    }),
    initI18Next(translation),
  ])
    .then(() => {
      const maintenanceModeWindowEnabled = maintenanceWindowActive();
      const isMaintenanceEnabled = maintenanceModeWindowEnabled || isMaintenanceModeEnabled;

      const { getRouteFromPath, router } = buildRouter(
        routes,
        appPath,
        isMaintenanceEnabled,
        environment,
      );

      if (!isMaintenanceEnabled) {
        store.send({
          type: StoreEvent.INITIALISE,
          data: { urlParams, source },
        });
      }

      log.info({ message: 'Successfully initialized third-party services' });

      createRoot(container).render(
        <StrictMode>
          <OTELProvider
            value={{
              namespace: 'olr',
              brandCode,
              version,
              serviceName,
              otelExporterEndpoint: otel.otelExporterEndpoint,
              environment,
              otelToken: otel.otelToken,
              hostName: globalThis.location?.hostname,
              apiBaseUrl,
            }}
          >
            <AppContextProvider
              value={{
                clientLogo,
                dGLogo,
                getRouteFromPath,
                tenantFeatures: resolvedTenantFeatures,
              }}
            >
              <PostHogProvider client={posthog}>
                <RouterProvider router={router} />
              </PostHogProvider>
            </AppContextProvider>
          </OTELProvider>
        </StrictMode>,
      );
      log.info({ message: 'Application started successfully' });
    })
    .catch((error: unknown) => {
      /** TODO - Consider how a render startup error should be handled */
      if (error instanceof RecaptchaError) {
        enhanceSpan(
          'olr',
          {
            event: 'recaptchaInitFailed',
            error: error.message,
          },
          true,
        );
        log.error({
          message: 'Failed to initialize third-party services during app startup',
          data: error,
        });
      } else {
        enhanceSpan(
          'olr',
          {
            event: 'appStartupFailed',
            error: error instanceof Error ? error.message : 'Unknown error',
          },
          true,
        );
        log.error({
          message: 'Unexpected error during app startup',
          data: error,
        });
      }
    });
};
